import { useContext, useEffect, useMemo, useState } from 'preact/hooks';
import { teadsApi } from '../../teads-api';
import { PreviewContext } from '../contexts/previewContext';
import { useAdLoaded } from './useAdLoaded';

export interface ITeadsContextExtension {
  tracking: ITeadsContextExtensionTracking;
  context: ITeadsContextExtensionContext;
}

export interface ITeadsContextExtensionTracking {
  endpoint: string;
  gid: number;
  cid: number;
  pid: number;
  env: string;
  slot: string;
  studio_cid: number;
  wid: string;
  wname: string;
  idfa: string;
  auctid: number;
  ad_id: number;
  ad_name: string;
  device: string;
}

export interface ITeadsContextExtensionContext {
  page: ITeadsContextExtensionContextPage;
  user: ITeadsContextExtensionContextUser;
}

export interface ITeadsContextExtensionContextPage {
  grapeshotKeywords: Array<string>;
}

export interface ITeadsContextExtensionContextUser {
  'city-name': string;
  'country-name': string;
  device: string;
  dma: string;
  region: string;
}

function replaceKeyword(
  text: string,
  keyword: KeywordType,
  value: string,
  regexPattern: string = `\\/Keyword \\[${keyword}]`
) {
  const regex = new RegExp(regexPattern, 'g');

  return text.replace(regex, value);
}

function capitalize(value: string) {
  return value ? value[0].toUpperCase() + value.substr(1) : value;
}

function capitalizeWithLocale(value: string, locale: string) {
  return value ? value[0].toLocaleUpperCase(locale) + value.substr(1) : value;
}

export const Keywords = ['Country', 'City', 'Platform', 'DMA', 'Region', 'DayOfWeek'] as const;
type KeywordType = (typeof Keywords)[number];
type KeywordsValues = { [key in KeywordType]: string };

const EmptyContextData: ITeadsContextExtensionContextUser = {
  'country-name': '',
  'city-name': '',
  device: '',
  dma: '',
  region: '',
};

function useKeywordValue(locale: string): KeywordsValues {
  const [contextData, setContextData] = useState<ITeadsContextExtension | null>(null);

  useEffect(() => {
    teadsApi.getContextData((err, info) => {
      if (err) return console.error(err);

      setContextData(info);
    });
  }, []);

  return useMemo(() => {
    const user = contextData?.context?.user ?? EmptyContextData;

    return {
      Country: capitalize(user['country-name']),
      City: capitalize(user['city-name']),
      Platform: user.device,
      DMA: capitalize(user.dma),
      Region: capitalize(user.region),
      DayOfWeek: new Date().toLocaleString(locale, { weekday: 'long' }),
    };
  }, [contextData, locale]);
}

export function useTextKeywords(text: string, locale: string) {
  const setAdLoaded = useAdLoaded();
  const keywordValue = useKeywordValue(locale);
  const preview = useContext(PreviewContext);
  const [result, setResult] = useState('');
  useEffect(() => {
    if (!keywordValue || typeof text !== 'string') {
      setAdLoaded();
      return;
    }

    let newText = text;

    const capitalizedDOW = capitalizeWithLocale(keywordValue['DayOfWeek'], locale);
    newText = replaceKeyword(newText, 'DayOfWeek', '$1' + capitalizedDOW, '^(\\s*)\\/Keyword \\[DayOfWeek]');
    newText = replaceKeyword(newText, 'DayOfWeek', '$1' + capitalizedDOW, '(\\.\\s*)\\/Keyword \\[DayOfWeek]');

    for (const keyword of Keywords) {
      newText = replaceKeyword(newText, keyword, keywordValue[keyword] || `[${keyword}]`);
    }

    setAdLoaded();
    setResult(newText);
  }, [text, keywordValue, locale]);
  return preview ? text : result;
}
